"use client";
import Image from "next/image";
import "home/Transitions.css";
import FacebookIllustration from "public/assets/heroIllustrations/facebookIllustration.svg";
import AmazonIllustration from "public/assets/heroIllustrations/amazonIllustration.svg";
import AirBnbIllustration from "public/assets/heroIllustrations/airbnbIllustration.svg";
import GoogleIllustration from "public/assets/heroIllustrations/googleIllustration.svg";
import NetflixIllustration from "public/assets/heroIllustrations/netflixIllustration.svg";
import NewCircles from "public/assets/heroIllustrations/newCircles.svg";
import { StarIcon } from "@heroicons/react/24/solid";
import { cx } from "lib/cx";
import { useRef } from "react";
import { useTailwindBreakpoints } from "lib/hooks/useTailwindBreakpoints";
import Link from "next/link";

const Hero = () => {
  return (
    <main className="px-4 font-montserrat md:px-8 lg:h-[calc(100vh-80px)]">
      <DesktopHero />
      <MobileHero />
    </main>
  );
};

export default Hero;

const DesktopHero = () => {
  return (
    <div className="hidden lg:flex">
      <div className="w-1/2">
        <h1 className="text-5xl font-bold text-[#030D33]">
          Elevate Your <span className="text-[#3375AE]">Career</span>
          <br /> With a Professionally <br /> Written Resume
        </h1>
        <p className="mt-8 text-3xl font-medium text-[#2D3659]">
          Professional writers, proven <br /> strategies, and tailored designs.
        </p>
        <div className="mt-8 flex space-x-4">
          <Link
            href="https://wa.me/918618147607"
            className="btn-gradient px-3 py-2"
          >
            Consult Now for Free
          </Link>
          <Link
            href="#plans"
            className="rounded-3xl bg-white px-3 py-2 text-[#515151] drop-shadow-lg"
          >
            View Plans
          </Link>
        </div>
        <div className="mt-16">
          <h4 className="text-xl font-medium text-[#2D3659]">Clients From</h4>
          <div className="mt-2 flex items-center">
            <FacebookIllustration
              className={cx(
                "fade-in-left-hero del-1",
                "h-auto md:w-[57px] lg:w-[63px]"
              )}
            />
            <AmazonIllustration
              className={cx(
                "fade-in-left-hero del-2 -ml-5",
                "h-auto md:w-[57px] lg:w-[63px]"
              )}
            />
            <AirBnbIllustration
              className={cx(
                "fade-in-left-hero del-3 -ml-3",
                "h-auto md:w-[57px] lg:w-[63px]"
              )}
            />
            <GoogleIllustration
              className={cx(
                "fade-in-left-hero del-4 -ml-4",
                "h-auto md:w-[57px] lg:w-[63px]"
              )}
            />
            <NetflixIllustration
              className={cx(
                "fade-in-left-hero del-5 -ml-4",
                "h-auto md:w-[57px] lg:w-[63px]"
              )}
            />
          </div>
        </div>
      </div>
      <div className="flex w-1/2 justify-end">
        <HeroIllustration />
      </div>
    </div>
  );
};

const MobileHero = () => {
  return (
    <div className="text-center lg:hidden">
      <h1 className="text-xl font-bold text-[#030D33] md:text-3xl">
        Elevate Your <span className="text-[#3375AE]">Career</span>
        <br /> With a Professionally <br /> Written Resume
      </h1>
      <HeroIllustration />
      <p className="mt-5text-xs font-medium text-[#2D3659] md:text-xl">
        Professional writers, proven <br /> strategies, and tailored designs.
      </p>
      <div className="mt-5 flex justify-center space-x-4 font-poppins text-[10px] md:text-sm lg:text-base">
        <Link
          href="https://wa.me/918618147607"
          className="btn-gradient w-[133px] whitespace-nowrap p-3 text-center md:w-[200px]"
        >
          Consult Now for Free
        </Link>
        <Link
          href="#plans"
          className="w-[133px] rounded-3xl bg-white p-3 text-[#515151] drop-shadow-lg md:w-[200px]"
        >
          View Plans
        </Link>
      </div>
    </div>
  );
};

const HeroIllustration = () => {
  const heightRef = useRef<HTMLDivElement>(null);
  const { isLg } = useTailwindBreakpoints();
  return (
    <div className="relative w-full max-lg:my-5 max-lg:h-[500px] max-md:h-[265px]">
      <div
        className="absolute right-0 top-0 z-20 flex h-[260px] w-[260px] items-center justify-center max-lg:right-1/2 max-lg:translate-x-1/2 md:h-[500px] md:w-[500px] lg:h-[490px] lg:w-[490px] xl:h-[570px] xl:w-[570px]"
        ref={heightRef}
      >
        <div className="relative z-10 h-[155px] w-[155px] rounded-full bg-[#F1F1F1] drop-shadow-md md:h-[335px] md:w-[335px] lg:h-[472px] lg:w-[472px]">
          {/* Stats: Selection Rate */}
          <div className="fade-in-top-hero absolute -left-12 top-7 z-20 flex h-[24px] w-[90px] items-center gap-2 rounded-md bg-white p-2 text-[4.5px] drop-shadow-sm md:h-[44px] md:w-[157px] md:gap-3 md:p-2 md:text-[10px] lg:-left-36 lg:h-[73px] lg:w-[275px] lg:rounded-xl lg:px-4 lg:py-1 lg:text-base">
            <div className="relative h-3 w-3 md:h-7 md:w-7 lg:h-10 lg:w-10">
              <Image
                src="/assets/resume-writing/rate-icon.png"
                alt="Rate Icon"
                fill
              />
            </div>
            <div className=" font-montserrat">
              <div className="font-semibold">81% Selection Rate</div>
              <div>
                <StarIcon className="inline h-2 w-2 text-yellow-400 md:h-3 md:w-3 lg:h-5 lg:w-5" />{" "}
                <span className="font-semibold">4.5</span>{" "}
                <span className="text-[4px] text-gray-500 md:text-[9px] lg:text-sm">
                  (528 Reviews)
                </span>
              </div>
            </div>
          </div>

          {/* Hero Person Image */}
          <div className="absolute -bottom-2 right-1/2 z-10 h-[186px] w-[119px] translate-x-1/2 md:-bottom-4 md:h-[390px] md:w-[245px] lg:-bottom-7 lg:h-[565px] lg:w-[362px]">
            <Image
              src="/assets/resume-writing/person.png"
              alt="Person"
              fill
              style={{ objectFit: "cover" }}
            />
          </div>

          {/* Stats: Number of Users */}
          <div className="fade-in-diagonal-right absolute -bottom-2 -right-2 z-40 flex h-[24px] w-[71px] items-center rounded-md bg-white p-2 text-[4.5px] drop-shadow-sm md:h-[44px] md:w-[130px] md:p-2 md:text-[10px] lg:-bottom-2 lg:-right-10 lg:h-[73px] lg:w-[217px] lg:rounded-xl lg:px-4 lg:py-1 lg:text-base">
            <div className=" flex flex-col items-start font-montserrat">
              <div className="font-semibold">1000+ Happy Clients</div>
              <div>
                <StarIcon className="inline h-2 w-2 text-yellow-400 md:h-3 md:w-3 lg:h-5 lg:w-5" />{" "}
                <span className="font-semibold">4.7</span>{" "}
                <span className="text-[4px] text-gray-500 md:text-[9px] lg:text-sm">
                  (157 Reviews)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <NewCircles
        className={cx(
          "absolute right-0 top-0 h-auto w-[260px] max-lg:right-1/2 max-lg:translate-x-1/2 md:w-[500px] lg:w-[490px] xl:w-[570px]",
          isLg && "rotating-circle"
        )}
      />
    </div>
  );
};
